import { apiRequest } from '@/shared/api';

export interface ChatSummary {
  dialog_id: string;
  channel: string;
  channel_chat_external_id: string;
  status: string;
  agata_paused_until?: string;
  role: string;
  display_name: string;
  phone: string;
  pipeline: string;
  stage: string;
  fields: Record<string, string>;
  last_message: string;
  last_activity_at: string;
}

export interface ChatListingAttachment {
  type: 'listing';
  id: string;
  title?: string;
  url?: string;
  photos?: string[];
  price_thb?: string | number;
  usd_price?: string | number;
  price_quote?: string | number;
  quote_currency?: string;
  district?: string;
  area_sqm?: string | number;
  property_type?: string;
}

export type ChatMessageAttachment =
  ChatListingAttachment | { type: string; [key: string]: unknown };

export interface ChatMessage {
  id: string;
  sender: string;
  content: string;
  lifecycle_status: string;
  created_at: string;
  run_id?: string | null;
  attachments?: ChatMessageAttachment[];
}

export interface CrmFunnel {
  name: string;
  stages: string[];
}

export interface CrmComment {
  id: number;
  text: string;
  author?: string;
  created_by?: number;
  created_at: string;
}

export interface CrmActivityItem {
  kind: 'comment' | 'field_change' | 'stage_change' | string;
  id: string;
  text?: string;
  author?: string;
  field?: string;
  old_value?: string;
  new_value?: string;
  source?: string;
  created_at: string;
}

export interface ChatDetail {
  dialog_id: string;
  interlocutor_id: string;
  card_id?: string;
  channel: string;
  channel_chat_external_id: string;
  status: string;
  agata_paused_until?: string;
  role: string;
  display_name: string;
  phone: string;
  pipeline: string;
  stage: string;
  fields: Record<string, string>;
  summary?: string;
  activity?: CrmActivityItem[];
  last_event?: string;
  last_activity_at: string;
}

export interface CrmContact {
  id: string;
  amocrm_contact_id?: number;
  name: string;
  phone?: string;
  email?: string;
  channel?: string;
  is_main?: boolean;
}

export interface CrmCard {
  id: string;
  dialog_id?: string;
  pipeline: string;
  stage: string;
  display_name: string;
  phone: string;
  channel: string;
  channel_chat_external_id?: string;
  status: string;
  role: string;
  last_event?: string;
  last_message: string;
  fields: Record<string, string>;
  comments?: CrmComment[];
  activity?: CrmActivityItem[];
  contacts?: CrmContact[];
  summary?: string;
  price?: number;
  responsible_name?: string;
  responsible_amocrm_user_id?: number;
  amocrm_lead_id?: number;
  updated_at: string;
}

const pollMs = 4000;

export const chatsPollInterval = pollMs;

function linkedUser(chatID?: string) {
  const id = chatID?.trim() ?? '';
  if (!id.toLowerCase().endsWith('@lid')) return '';
  return id.slice(0, -4).replace(/\D/g, '');
}

/** A phone or title the operator can see. WhatsApp LID ids are not numbers. */
export function shownPhone(value?: string, chatID?: string) {
  const text = value?.trim() ?? '';
  if (!text || text.toLowerCase().includes('@lid')) return '';
  const user = linkedUser(chatID);
  const digits = text.replace(/\D/g, '');
  if (user && digits === user) return '';
  return text;
}

/** True when the string is mostly a phone the operator can dial. */
export function looksLikePhoneTitle(value?: string) {
  const text = value?.trim() ?? '';
  if (!text || text.toLowerCase().includes('@lid')) return false;
  const compact = text.replace(/[\s\-().]/g, '');
  const digits = compact.replace(/\D/g, '');
  if (digits.length < 8) return false;
  // WhatsApp chat ids like 66812345678@c.us
  if (compact.toLowerCase().endsWith('@c.us')) return digits.length >= 8;
  return digits.length >= Math.ceil(compact.length * 0.7);
}

export function chatTitle(chat: {
  display_name?: string;
  phone?: string;
  channel_chat_external_id?: string;
}): string {
  const chatID = chat.channel_chat_external_id;
  const name = shownPhone(chat.display_name, chatID);
  // Never treat a raw phone / chat id as a person name when a real display_name is empty.
  if (name && !looksLikePhoneTitle(name)) return name;
  const phone = shownPhone(chat.phone, chatID);
  if (phone && looksLikePhoneTitle(phone)) return phone;
  const externalID = shownPhone(chatID, chatID);
  // Widget visitor ids (and eval ids like live2-hot-…) are not human titles —
  // leave empty so the UI shows the generic «Лид» label.
  if (!externalID || !looksLikePhoneTitle(externalID)) return '';
  return externalID.length > 22 ? `${externalID.slice(0, 22)}…` : externalID;
}

export async function listChats(): Promise<ChatSummary[]> {
  const body = await apiRequest<{ chats: ChatSummary[] }>('/chats');
  return body.chats ?? [];
}

export async function getChat(dialogId: string): Promise<ChatDetail> {
  return apiRequest<ChatDetail>(`/chats/${dialogId}`);
}

export async function listChatMessages(dialogId: string): Promise<ChatMessage[]> {
  const body = await apiRequest<{ messages: ChatMessage[] }>(`/chats/${dialogId}/messages`);
  return body.messages ?? [];
}

export type AgataAction = 'pause_60m' | 'stop' | 'resume';
export type PauseAgataOnSend = 'none' | 'pause_60m' | 'stop';

export async function controlChatAgata(
  dialogId: string,
  action: AgataAction,
): Promise<ChatDetail> {
  return apiRequest<ChatDetail>(`/chats/${encodeURIComponent(dialogId)}/agata`, {
    method: 'POST',
    body: { action },
  });
}

export async function sendChatMessage(
  dialogId: string,
  input: { content: string; pause_agata?: PauseAgataOnSend },
): Promise<ChatMessage> {
  return apiRequest<ChatMessage>(`/chats/${encodeURIComponent(dialogId)}/messages`, {
    method: 'POST',
    body: input,
  });
}

export async function listFunnels(): Promise<CrmFunnel[]> {
  const body = await apiRequest<{ funnels: CrmFunnel[] }>('/crm/funnels');
  return body.funnels ?? [];
}

export interface SalesNote {
  id: string;
  card_id: string;
  dialog_id?: string;
  pipeline: string;
  from_stage: string;
  to_stage: string;
  body: string;
  created_at: string;
}

export async function listSalesNotes(cardOrDialogId: string): Promise<SalesNote[]> {
  const body = await apiRequest<{ notes: SalesNote[] }>(
    `/crm/cards/${encodeURIComponent(cardOrDialogId)}/sales-notes`,
  );
  return body.notes ?? [];
}

export async function moveCrmStage(
  cardId: string,
  input: { stage: string; note: string },
): Promise<SalesNote> {
  const body = await apiRequest<{ note: SalesNote }>(
    `/crm/cards/${encodeURIComponent(cardId)}/stage`,
    { method: 'POST', body: input },
  );
  return body.note;
}

export async function createCrmCard(input: {
  pipeline: string;
  stage?: string;
  display_name: string;
  phone?: string;
  price?: number;
}): Promise<CrmCard> {
  return apiRequest<CrmCard>('/crm/cards', { method: 'POST', body: input });
}

export async function listCrmCards(pipeline: string): Promise<CrmCard[]> {
  const query = pipeline ? `?pipeline=${encodeURIComponent(pipeline)}` : '';
  const body = await apiRequest<{ cards: CrmCard[] }>(`/crm/cards${query}`);
  return body.cards ?? [];
}

export async function getCrmCard(cardId: string): Promise<CrmCard> {
  return apiRequest<CrmCard>(`/crm/cards/${encodeURIComponent(cardId)}`);
}

export interface UpdateCrmCardInput {
  display_name?: string;
  summary?: string;
  price?: number;
  lead_bucket?: string;
  lead_source?: string;
  responsible_amocrm_user_id?: number;
  role?: string;
  status?: string;
}

export async function updateCrmCard(cardId: string, input: UpdateCrmCardInput): Promise<CrmCard> {
  return apiRequest<CrmCard>(`/crm/cards/${encodeURIComponent(cardId)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function addCrmCardActivity(
  cardId: string,
  input: { text: string },
): Promise<CrmCard> {
  return apiRequest<CrmCard>(`/crm/cards/${encodeURIComponent(cardId)}/activity`, {
    method: 'POST',
    body: input,
  });
}
